import {
  IAthenaImportLogListParams,
  IAthenaImportLogListResponse,
} from "types/AthenaIntegration";
import {
  IHstImportBatchDetailResponse,
  IHstImportBatchListParams,
  IHstImportBatchListResponse,
  IHstImportFileUrlResponse,
  IHstImportUndoRequest,
  IHstImportUndoResponse,
  HstImportFileKind,
} from "types/HstImportBatch";

import { api } from ".";

// HST Import row result
export interface IHSTImportRowResult {
  patIdVisit: string;
  dos: string;
  patientName: string;
  success: boolean;
  orderId?: string;
  skipped?: boolean;
  updated?: boolean;
  existingOrderId?: string;
  errors?: Record<string, string>;
}

// HST Import overall result
export interface IHSTImportResult {
  totalRows: number;
  successCount: number;
  updateCount: number;
  errorCount: number;
  duplicateCount: number;
  csvDuplicateCount: number;
  skippedPayerCount: number;
  noInsuranceMatchCount: number;
  // CLE-3068 — rows whose PayerID matched no payer; not imported, also in errorCount.
  unmappedPayerCount: number;
  results: IHSTImportRowResult[];
  // The HstImportBatch this import was recorded as (Integrations → HST Imports).
  batchId?: string;
  undoWindowDays?: number;
}

// HST Import response
export interface IHSTImportResponse {
  data: IHSTImportResult;
  message: string;
}

// HST Import request params
export interface IHSTImportRequest {
  accountId: string;
  scheduledAppointments: File;
  insuranceInfo: File;
}

// HST Cancel (SC1001) outcome per row
export type IHSTCancelOutcome =
  | "CANCELED"
  | "ALREADY_CANCELED"
  | "NOT_FOUND"
  | "AMBIGUOUS"
  | "REVIEW_REQUIRED"
  | "INVALID_ROW";

// HST Cancel row result
export interface IHSTCancelRowResult {
  rowNumber: number;
  patientName: string;
  srgyDate: string;
  cancelReason: string;
  outcome: IHSTCancelOutcome;
  isNoShow: boolean;
  orderId?: string;
  matchedOrderIds?: string[];
  message?: string;
}

// HST Cancel overall result
export interface IHSTCancelResult {
  totalRows: number;
  canceledCount: number;
  alreadyCanceledCount: number;
  notFoundCount: number;
  ambiguousCount: number;
  reviewRequiredCount: number;
  invalidRowCount: number;
  noShowCount: number;
  reportedTotal: number | null;
  results: IHSTCancelRowResult[];
}

// HST Cancel response
export interface IHSTCancelResponse {
  data: IHSTCancelResult;
  message: string;
}

// HST Cancel request params
export interface IHSTCancelRequest {
  accountId: string;
  cancelledAppointments: File;
}

// Athena Import row result
export interface IAthenaImportRowResult {
  dedupKey: string;
  dos: string;
  patientName: string;
  accountName: string;
  success: boolean;
  orderId?: string;
  skipped?: boolean;
  updated?: boolean;
  existingOrderId?: string;
  errors?: Record<string, string>;
}

// Athena Import overall result
export interface IAthenaImportResult {
  totalRows: number;
  totalGroups: number;
  successCount: number;
  updateCount: number;
  errorCount: number;
  duplicateCount: number;
  unmappedPayerCount: number;
  // CLE-2633 — appointments the import deliberately declined to turn into orders.
  noFacilityCount: number;
  noCptCount: number;
  // CLE-2674 — Medicare with a supplement secondary: no patient balance, so no order.
  medicareSupplementCount: number;
  results: IAthenaImportRowResult[];
}

/**
 * CLE-2633 — the upload now returns immediately with an id and the import runs server-side.
 * Aptible's nginx closes the connection at 60s, so a real report (2,727 rows, ~2 minutes) used to
 * return a 504 while the work carried on — taking the per-row report down with it.
 */
export interface IAthenaImportStartResponse {
  data: { importId: string };
  message: string;
}

export type AthenaImportStatus =
  | "pending"
  | "processing"
  | "completed"
  | "failed";

/** The AthenaImport row the SPA polls until it reaches a terminal status. */
export interface IAthenaImportRecord extends IAthenaImportResult {
  id: string;
  status: AthenaImportStatus;
  filename: string | null;
  errorMessage: string | null;
  startedAt: string | null;
  completedAt: string | null;
}

export interface IAthenaImportStatusResponse {
  data: IAthenaImportRecord;
  message: string;
}

// Athena Import request params
export interface IAthenaImportRequest {
  athenaReport: File;
  // CLE-2948 — the account the dialog was opened from, recorded for Integrations → Athena.
  accountId?: string;
}

const integrationQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    hstImport: builder.mutation<IHSTImportResponse, IHSTImportRequest>({
      query: ({ accountId, scheduledAppointments, insuranceInfo }) => {
        const formData = new FormData();
        formData.append("accountId", accountId);
        formData.append("scheduledAppointments", scheduledAppointments);
        formData.append("insuranceInfo", insuranceInfo);

        return {
          url: "/integration/hst/import",
          method: "POST",
          body: formData,
        };
      },
      // Each import is recorded as a batch, so the Integrations list is stale after one.
      invalidatesTags: [{ type: "HstImportBatches", id: "LIST" }],
    }),
    hstCancel: builder.mutation<IHSTCancelResponse, IHSTCancelRequest>({
      query: ({ accountId, cancelledAppointments }) => {
        const formData = new FormData();
        formData.append("accountId", accountId);
        formData.append("cancelledAppointments", cancelledAppointments);

        return {
          url: "/integration/hst/cancellations",
          method: "POST",
          body: formData,
        };
      },
    }),
    athenaImport: builder.mutation<
      IAthenaImportStartResponse,
      IAthenaImportRequest
    >({
      query: ({ athenaReport, accountId }) => {
        const formData = new FormData();
        formData.append("athenaReport", athenaReport);
        if (accountId) formData.append("accountId", accountId);

        return {
          url: "/integration/athena/import",
          method: "POST",
          body: formData,
        };
      },
      // Each import is a new row in the Integrations → Athena log.
      invalidatesTags: [{ type: "AthenaImports", id: "LIST" }],
    }),
    athenaImportStatus: builder.query<IAthenaImportStatusResponse, string>({
      query: (importId) => ({
        url: `/integration/athena/import/${importId}`,
        method: "GET",
      }),
    }),
    // CLE-2948 — Athena imports log, SuperAdmin only (Integrations → Athena). No `results` in it.
    getAthenaImports: builder.query<
      IAthenaImportLogListResponse,
      IAthenaImportLogListParams
    >({
      query: ({ accountId, page, pageSize }) => ({
        url: "/integration/athena/imports",
        method: "GET",
        params: {
          ...(accountId ? { accountId } : {}),
          page,
          pageSize,
        },
      }),
      providesTags: [{ type: "AthenaImports", id: "LIST" }],
    }),
    // HST import batches — SuperAdmin only (Integrations → HST Imports).
    getHstImportBatches: builder.query<
      IHstImportBatchListResponse,
      IHstImportBatchListParams
    >({
      query: ({ accountId, page, pageSize }) => ({
        url: "/integration/hst/imports",
        method: "GET",
        params: {
          ...(accountId ? { accountId } : {}),
          page,
          pageSize,
        },
      }),
      providesTags: [{ type: "HstImportBatches", id: "LIST" }],
    }),
    getHstImportBatch: builder.query<IHstImportBatchDetailResponse, string>({
      query: (id) => ({
        url: `/integration/hst/imports/${id}`,
        method: "GET",
      }),
      providesTags: (_result, _error, id) => [{ type: "HstImportBatches", id }],
    }),
    // A 5-minute signed S3 URL, so it is always fetched fresh (lazy, never cached).
    getHstImportFileUrl: builder.query<
      IHstImportFileUrlResponse,
      { id: string; which: HstImportFileKind }
    >({
      query: ({ id, which }) => ({
        url: `/integration/hst/imports/${id}/files/${which}`,
        method: "GET",
      }),
      keepUnusedDataFor: 0,
    }),
    undoHstImportBatch: builder.mutation<
      IHstImportUndoResponse,
      IHstImportUndoRequest
    >({
      query: ({ id, confirmAccountName }) => ({
        url: `/integration/hst/imports/${id}/undo`,
        method: "POST",
        body: { confirmAccountName },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "HstImportBatches", id: "LIST" },
        { type: "HstImportBatches", id },
      ],
    }),
  }),
});

export const {
  useHstImportMutation,
  useHstCancelMutation,
  useAthenaImportMutation,
  useAthenaImportStatusQuery,
  useLazyAthenaImportStatusQuery,
  useGetAthenaImportsQuery,
  useGetHstImportBatchesQuery,
  useGetHstImportBatchQuery,
  useLazyGetHstImportFileUrlQuery,
  useUndoHstImportBatchMutation,
} = integrationQueries;
